import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { CtBinaryOperator, CtModelConfiguration, CTModelDatatableFilter, CtModelDatatableOperators, CtModelService, CtSchedulerConfiguration, CtSchedulerEvent } from '@ctsolution/ct-framework';
import { SchedulerRouteData } from '../../../_core/route-data/schedule.route-data';
import { SchedulerService } from '../scheduler.service';
import { Router } from '@angular/router';
import { CalendarEvent } from 'angular-calendar';
import { CTMGeneralService } from '../../../_core/lib/general.service';
import { EventPathParam } from '../../../_core/classes/event-path-param';

/**
 * Interfaccia per i dati passati al dialog dello scheduler
 */
export interface SchedulerDialogData {
  /** Parametri dell'entità di origine (Lead, WorkOrder, etc.) */
  param?: EventPathParam | null;
  /** ID dell'evento da modificare (se presente, apre in modalità edit) */
  idEvent?: string | null;
  /** Callback da eseguire prima di aprire il dialog (es. salvataggio form parent) */
  beforeOpenAction?: (() => void | Promise<void>) | null;
}

/**
 * Componente modale che wrappa lo SchedulerComponent
 * Permette di aprire lo scheduler in una modale invece di navigare a una nuova pagina
 * 
 * Funzionalità:
 * - Mostra il calendario completo in modalità modale
 * - Gestisce la creazione e modifica eventi
 * - Chiude automaticamente la modale dopo il salvataggio
 * - Emette un evento di refresh per aggiornare il componente parent
 */
@Component({
  selector: 'app-scheduler-dialog',
  templateUrl: './scheduler-dialog.component.html',
  styleUrls: ['./scheduler-dialog.component.scss']
})
export class SchedulerDialogComponent implements OnInit {

  /** Configurazione del componente scheduler */
  schedulerConfiguration: CtSchedulerConfiguration = CtSchedulerConfiguration
    .create()
    .setLocale('it')
    .setOnCreate(async (value: any) => this.onCreate(value))
    .setOnEdit((value: CalendarEvent) => this.onEdit(value))
    .setExportEnabled(true)
    .setOnExport((event: any) => {
      const body: CtModelDatatableOperators = this.getModelDatatableOperators(event, this.schedulerConfiguration);
      this.modelService.export(this.configuration, body, { action: 'ExportExcelScheduler' })
    });

  /** Configurazione del modello ct-framework */
  configuration: CtModelConfiguration<any> = CtModelConfiguration.create()
    .setRouteData(SchedulerRouteData())
    .setCTSchedulerConfiguration(this.schedulerConfiguration)

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: SchedulerDialogData,
    private dialogRef: MatLegacyDialogRef<SchedulerDialogComponent>,
    private schedulerService: SchedulerService,
    private router: Router,
    private generalService: CTMGeneralService,
    private modelService: CtModelService<any>
  ) { }

  ngOnInit() {
    // Se c'è un IdEvent, carica l'evento e aprilo in modalità edit
    if (this.data.idEvent) {
      this.loadAndEditEvent(this.data.idEvent);
    }
  }

  /**
   * Carica un evento esistente dal backend e apre il form di modifica
   * @param eventId ID dell'evento da caricare
   */
  private loadAndEditEvent(eventId: string) {
    this.generalService.getList({
      controller: 'scheduler',
      filters: [
        CTModelDatatableFilter
          .create()
          .setField('Oid')
          .setValue(eventId)
          .setOperatorType(CtBinaryOperator.Equal)
      ]
    }).subscribe(response => {
      if (response.Result?.DataSource?.[0]) {
        this.onEditFly(response.Result.DataSource[0]);
      }
    });
  }

  /**
   * Gestisce la creazione di un nuovo evento
   * @param value Dati dell'evento dal calendario (data/ora selezionata)
   */
  async onCreate(value: any) {
    const ret = await this.schedulerService.createEventFromPath(
      this.configuration, 
      this.data.param, 
      null, 
      value
    );

    // Se l'evento è stato salvato con successo, chiudi la modale e notifica il parent
    if (ret) {
      this.dialogRef.close({ refresh: true });
    }
  }

  /**
   * Gestisce la modifica di un evento esistente cliccato sul calendario
   * @param value Evento del calendario da modificare
   */
  async onEdit(value: CalendarEvent) {
    const ret = await this.schedulerService.createEventFromPath(
      this.configuration, 
      null, 
      (<any>value).data
    );

    // Se l'evento è stato salvato con successo, chiudi la modale e notifica il parent
    if (ret) {
      this.dialogRef.close({ refresh: true });
    }
  }

  /**
   * Gestisce la modifica di un evento caricato direttamente (da link/queryParams)
   * Usato quando si apre la modale con un IdEvent specifico
   * @param value Dati completi dell'evento dal backend
   */
  async onEditFly(value: CtSchedulerEvent) {
    const ret = await this.schedulerService.createEventFromPath(
      this.configuration, 
      null, 
      value
    );

    // Se l'evento è stato salvato con successo, chiudi la modale e notifica il parent
    if (ret) {
      this.dialogRef.close({ refresh: true });
    }
  }

  /**
   * Chiude la modale senza salvare
   */
  close() {
    this.dialogRef.close({ refresh: false });
  }

  /**
   * Costruisce i filtri per l'export Excel del calendario
   * @param parameter Parametri della vista corrente (date start/end, tipo vista)
   * @param configuration Configurazione dello scheduler
   * @returns Operatori di filtro per la query
   */
  private getModelDatatableOperators(parameter: any, configuration: CtSchedulerConfiguration | null): CtModelDatatableOperators {
    const body = CtModelDatatableOperators.create();
    body.Pagination?.setPage(1)?.setElementsForPage(null);

    const params = {
      start: parameter.Start,
      end: parameter.End
    };

    params.start.setDate(params.start.getDate() + 1);

    if (configuration) {
      switch (configuration?.view) {
        case "week":
          this.setWeekFilter(configuration?.viewDate, params)
          break;
        case "day":
          this.setDayFilter(configuration?.viewDate, params)
          break;
      }
    }

    params.start.setUTCHours(0, 0, 0, 0)
    params.end.setUTCHours(23, 59, 59, 999)

    const filters = [
      CTModelDatatableFilter.create()
        .setField('Start>=? and End<=?')
        .setValue([params.start, params.end])
        .setOperatorType(CtBinaryOperator.AndOperator)
    ];

    body.setFilters(filters);
    return body;
  }

  /**
   * Calcola le date per il filtro della vista settimanale
   * @param date Data di riferimento
   * @param params Oggetto da popolare con start/end
   */
  private setWeekFilter(date: Date, params: any) {
    const firstDayOfWeek = 0; // Domenica
    const day = date.getDay();
    const diffToMonday = (day + 7 - firstDayOfWeek) % 7;

    const start = new Date(date);
    start.setDate(date.getDate() - diffToMonday);

    const end = new Date(start);
    end.setUTCDate(start.getDate() + 6);

    params.start = start;
    params.end = end;
  }

  /**
   * Calcola le date per il filtro della vista giornaliera
   * @param date Data di riferimento
   * @param params Oggetto da popolare con start/end
   */
  private setDayFilter(date: Date, params: any) {
    const start = new Date(date);
    const end = new Date(date);

    params.start = start;
    params.end = end;
  }
}
